import React, { useEffect, useState } from 'react';
import {
  Tabs, Tab, Box, Paper, Snackbar, Alert, Button, Typography, CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import RoleplayDetailsTab, { RoleplayDetailsValues } from '../components/AdminRoleplayDetailsTab';
import PageHeader from '../components/PageHeader';
import AdminPromptManagementTab, { PromptManagementFormValues, PromptFormErrors } from '../components/AdminPromptManagementTab';
import CurrentAssignmentsTab from '../components/AdminCurrentAssignmentTab';
import AssignScenarioTab from '../components/AdminAssignScenarioTab';
import { useAssessmentService, AssessmentType } from '../services/assessmentService';
import usePromptService, { ScorePromptType } from '../services/promptService';
import { transcode } from 'buffer';

const initialRoleplay: RoleplayDetailsValues = {
  title: '',
  description: '',
  meetingSummary: '',
  duration: '',
  promptTemplateId: undefined,
  goals: [''],
  personaName: '',
  personaRole: '',
  personaBehaviors: '',
  personaBio: '',
  voiceName: '',
  voiceGender: '',
  companyName: '',
  companyIndustry: '',
  companySize: '',
  companyLocations: '',
  companyTechProfile: '',
  companyChallenges: '',
  scenarioMeetingType: '',
  scenarioCustomerType: '',
  scenarioHistory: '',
  scenarioInitiatives: '',
  scenarioChallenges: '',
  scenarioConstraints: '',
};

interface CreateAssessmentDto extends RoleplayDetailsValues, PromptManagementFormValues {}

const initialPrompt: PromptManagementFormValues = {
  prompt: '',
  name: '',
  listening: '',
  rapport: '',
  valueArticulation: '',
  discovery: '',
  nextSteps: '',
};

interface AssessmentTabsProps {
  mode?: 'create' | 'edit';
}

function promptFromApi(scorePrompt: ScorePromptType): PromptManagementFormValues {
  return {
    prompt: scorePrompt.prompt ?? '',
    name: scorePrompt.name ?? '',
    listening: scorePrompt.listening ?? '',
    rapport: scorePrompt.rapport ?? '',
    valueArticulation: scorePrompt.value_articulation ?? '',
    discovery: scorePrompt.discovery ?? '',
    nextSteps: scorePrompt.next_steps ?? '',
  };
}

function validateRoleplayFields(values: RoleplayDetailsValues): Record<string, string> {
  const errors: Record<string, string> = {};
  const stringFields: (keyof RoleplayDetailsValues)[] = [
    'title', 'description', 'meetingSummary', 'duration', 'personaName', 'personaRole',
    'personaBehaviors', 'personaBio', 'voiceName', 'companyName', 'companyIndustry',
    'companySize', 'companyLocations', 'companyTechProfile', 'companyChallenges',
    'scenarioMeetingType', 'scenarioCustomerType', 'scenarioHistory', 'scenarioInitiatives',
    'scenarioChallenges', 'scenarioConstraints'
  ];
  stringFields.forEach(field => {
    const val = values[field];
    if (typeof val !== 'string' || !val.trim()) {
      errors[field] = "Required";
    }
  });
  if (
    !Array.isArray(values.goals) ||
    values.goals.length === 0 ||
    values.goals.some(g => typeof g !== 'string' || !g.trim())
  ) {
    errors.goals = "At least one non-empty goal is required";
  }
  return errors;
}

function validatePromptFields(values: PromptManagementFormValues): PromptFormErrors {
  const errors: PromptFormErrors = {};
  ([
    'name',
    'prompt',
    'listening',
    'rapport',
    'valueArticulation',
    'discovery',
    'nextSteps'
  ] as (keyof PromptManagementFormValues)[]).forEach(field => {
    const val = values[field];
    if (typeof val !== "string" || !val.trim()) {
      errors[field] = "Required";
    }
  });
  return errors;
}

function mapRoleplayValues(data: AssessmentType): RoleplayDetailsValues {
  const getString = (value: any) => (typeof value === 'string' ? value : '');
  const getStringArray = (value: any) => Array.isArray(value) ? value.map(String) : [''];
  return {
    title: getString(data.title),
    description: getString(data.description),
    meetingSummary: getString(data.meetingSummary),
    duration: data.duration !== undefined && data.duration !== null ? String(data.duration) : '',
    promptTemplateId: data.promptTemplateId,
    courseId: data.courseId,
    goals: getStringArray(data.goals),
    personaName: getString(data.personaName),
    personaRole: getString(data.personaRole),
    personaBehaviors: getString(data.personaBehaviors),
    personaBio: getString(data.personaBio),
    voiceName: getString(data.voiceName),
    voiceGender: getString(data.voiceGender),
    companyName: getString(data.companyName),
    companyIndustry: getString(data.companyIndustry),
    companySize: getString(data.companySize),
    companyLocations: getString(data.companyLocations),
    companyTechProfile: getString(data.companyTechProfile),
    companyChallenges: getString(data.companyChallenges),
    scenarioMeetingType: getString(data.scenarioMeetingType),
    scenarioCustomerType: getString(data.scenarioCustomerType),
    scenarioHistory: getString(data.scenarioHistory),
    scenarioInitiatives: getString(data.scenarioInitiatives),
    scenarioChallenges: getString(data.scenarioChallenges),
    scenarioConstraints: getString(data.scenarioConstraints),
  };
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}
function TabPanel({ children, value, index }: Readonly<TabPanelProps>) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Utility function for collecting all field errors
function collectAllFieldErrors(
  roleplayFieldErrors: Record<string, string>,
  promptFieldErrors: PromptFormErrors
) {
  return [
    ...Object.entries(roleplayFieldErrors).map(([key, val]) => `${key}: ${val}`),
    ...Object.entries(promptFieldErrors).map(([key, val]) => `${key}: ${val}`)
  ];
}

function prettifyKey(key: string): string {
  // Insert space before all caps, then capitalize the first letter
  return key
    .replaceAll(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase());
}

export default function AssessmentTabs({ mode = 'create' }: Readonly<AssessmentTabsProps>) {
  const navigate = useNavigate();
  const { scenario_id } = useParams<{ scenario_id: string }>();
  const { createAssessment, fetchAssessment, updateAssessment } = useAssessmentService();
  const { fetchScorePromptForScenario } = usePromptService();

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [roleplayValues, setRoleplayValues] = useState<RoleplayDetailsValues>(initialRoleplay);
  const [roleplayErrors, setRoleplayErrors] = useState<Record<string, string>>({});
  const [promptValues, setPromptValues] = useState<PromptManagementFormValues>(initialPrompt);
  const [promptErrors, setPromptErrors] = useState<PromptFormErrors>({});
  const [loadingPrompt, setLoadingPrompt] = useState<boolean>(false);
  const [assessment, setAssessment] = useState<AssessmentType | null>(null);
  const [loading, setLoading] = useState<boolean>(mode === 'edit');
  const [saving, setSaving] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    messages: string[];
    severity: 'error' | 'success';
  }>({
    open: false, messages: [], severity: 'error'
  });

  const isAssignmentsEnabled = mode === 'edit' && !!assessment;

  useEffect(() => {
    if (mode !== 'edit' || !scenario_id) return;
    setLoading(true);
    fetchAssessment(decodeURIComponent(scenario_id))
      .then(async (data: AssessmentType) => {
        setAssessment(data);
        setRoleplayValues(mapRoleplayValues(data));
        setLoadingPrompt(true);
        await handlePromptFetching(Number.parseInt(data.id));
        setLoadingPrompt(false);
      })
      .catch((err: Error) =>
        setSnackbar({
          open: true,
          messages: [err.message],
          severity: 'error'
        }))
      .finally(() => setLoading(false));
  }, [mode, scenario_id]);

  async function handlePromptFetching(id?: number) {
    if (id) {
      try {
        const promptRes = await fetchScorePromptForScenario(Number(id));
        setPromptValues(promptFromApi(promptRes));
      } catch (e: any) {
        console.log(`AssessmentTabs Page: Error while fetching Score Prompt - ${e}`);
        setPromptValues(initialPrompt);
      }
    } else {
      setPromptValues(initialPrompt);
    }
  }

  function showSnackbar(messages: string[], severity: 'error' | 'success') {
    setSnackbar({ open: true, messages, severity });
  }

  function handleTabChange(_: React.SyntheticEvent, idx: number) {
    if (idx === 1 && mode === 'create') {
      const errors = validateRoleplayFields(roleplayValues);
      setRoleplayErrors(errors);
      if (Object.keys(errors).length > 0) {
        showSnackbar(['Please fill all required roleplay fields.'], 'error');
        return;
      }
    }
    setTabIndex(idx);
  }

  async function handleSave(isEdit: boolean) {
    const roleplayFieldErrors = validateRoleplayFields(roleplayValues);
    const promptFieldErrors = validatePromptFields(promptValues);

    setRoleplayErrors(roleplayFieldErrors);
    setPromptErrors(promptFieldErrors);

    const allFieldErrors = collectAllFieldErrors(roleplayFieldErrors, promptFieldErrors);
    if (allFieldErrors.length > 0) {
      showSnackbar(["Fix the following errors:", ...allFieldErrors], 'error');
      return;
    }

    setSaving(true);
    try {
      let result: AssessmentType;
      if (isEdit) {
        const assessmentData: AssessmentType = {
          ...(assessment as AssessmentType),
          ...roleplayValues,
          ...promptValues,
        };
        result = await updateAssessment(assessmentData);
        setAssessment(result);
        showSnackbar(['Assessment updated!'], 'success');
      } else {
        const assessmentData: CreateAssessmentDto = {
          ...roleplayValues,
          ...promptValues,
        };
        result = await createAssessment(assessmentData);
        showSnackbar(['Assessment created!'], 'success');
        setTimeout(() => navigate(`/admin/assessments/${encodeURIComponent(result.id ?? '')}`), 1200);
      }
    } catch (e: any) {
      let messages = [];
      if (e.response?.data?.detail) {
        messages.push(e.response.data.detail);
      } else if (e.message) {
        messages.push(e.message);
      } else {
        messages.push(isEdit ? 'Update failed' : 'Create failed');
      }
      showSnackbar(messages, 'error');
    } finally {
      setSaving(false);
    }
  }

  function renderTabPanels() {
    return (
      <Box  sx={{
      overflow: 'auto'
      }}>
        <TabPanel value={tabIndex} index={0}>
          <RoleplayDetailsTab
            mode={mode}
            values={roleplayValues}
            onChange={setRoleplayValues}
            editable={mode === 'create'}
            saving={saving}
            errors={roleplayErrors}
          />
          {mode === 'edit' && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={saving}
                onClick={() => handleSave(true)}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          {loadingPrompt ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <AdminPromptManagementTab
              values={promptValues}
              onChange={setPromptValues}
              mode={mode}
              editable={mode === 'create'}
              errors={promptErrors}
              saving={saving}
              onSave={() => handleSave(mode === 'edit')}
            />
          )}
          {mode === 'edit' && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={saving}
                onClick={() => handleSave(true)}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={tabIndex} index={2}>
          {isAssignmentsEnabled && assessment
            ? <AssignScenarioTab assessment={assessment} />
            : <Typography variant="body2" color="text.secondary">Create assessment to assign scenario.</Typography>
          }
        </TabPanel>
        <TabPanel value={tabIndex} index={3}>
          {isAssignmentsEnabled && assessment?.id
            ? <CurrentAssignmentsTab assessmentId={Number(assessment.id)} />
            : <Typography variant="body2" color="text.secondary">Create assessment to manage assignments.</Typography>
          }
        </TabPanel>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      overflow: 'auto',
      width: '100%',
      py: 8,
      p: 4,
      height: '100vh',
    }}>
      <Paper sx={{
        py: 4, width: { xs: '95%', md: '95%', lg: '95%', xl: '65%' }
      }}>
        <Paper sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', p: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <PageHeader
              title={
                (roleplayValues.title?.trim())
                  ? roleplayValues.title
                  : "Create New Assessment"
              }
              subtitle=""
              onBack={() => mode === 'create' ? navigate(-1) : navigate("/admin")}
              showBackButton={true}
            />
          </Box>
          {mode !== 'create' && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  if (assessment?.id) {
                    const url = `${globalThis.window.location.origin}/assign/${assessment.id}`;
                    navigator.clipboard.writeText(url);
                  }
                }}
                sx={{ ml: 2 }}
              >
                Copy Assignment Link
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(
                  "/admin/assessments/create",
                  {
                    state: {
                      roleplayValues,
                      promptValues
                    }
                  }
                )}
                sx={{ ml: 2 }}
              >
                Create Copy
              </Button>
            </>
          )}
        </Paper>
        <Paper sx={{ p: 4 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="Assessment Tabs"
            sx={{ mb: 2 }}
          >
            <Tab label="Roleplay Details" />
            <Tab label="Rubric Details" />
            <Tab label="Assign Scenario" disabled={!isAssignmentsEnabled} />
            {isAssignmentsEnabled && (
              <Tab label="Current Assignments" disabled={!isAssignmentsEnabled} />
            )}
          </Tabs>
          {loading
            ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : renderTabPanels()
          }
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity}>
            {snackbar.messages.length === 1 ? (
              (() => {
                const msg = snackbar.messages[0];
                if (msg.includes(':')) {
                  const [field, ...rest] = msg.split(':');
                  return <span><b>{prettifyKey(field)}</b>: {rest.join(':').trim()}</span>;
                }
                return msg;
              })()
            ) : (
              <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                {snackbar.messages.map((msg, i) => {
                  if (msg.includes(':')) {
                    const [field, ...rest] = msg.split(':');
                    return (
                      <li key={`${msg}-${i}`}>
                        <b>{prettifyKey(field)}</b>: {rest.join(':').trim()}
                      </li>
                    );
                  }
                  return <li key={`${msg}-${i}`}>{msg}</li>;
                })}
              </ul>
            )}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}